// Ordre des contacts d'une fiche : d'abord ceux à contacter en premier.
import type { ContactLookup, ProjectContactLink } from "../types";

// Place dans la chaîne : une correction d'un utilisateur vaut certitude ; sans place, rien.
const confidence = (contact: ProjectContactLink) =>
  !contact.decision_role
    ? -1
    : contact.decision_method === "manual"
      ? 1
      : (contact.decision_confidence ?? 0);

const reachable = (contact: ProjectContactLink) =>
  !!contact.email_jsonb?.length || !!contact.phone_jsonb?.length;

/** Bailleurs à la fin ; puis les personnes qui nomment le projet dans leur fonction ; puis confiance de la place
 * décroissante ; puis joignables d'abord. Tri stable. */
export const sortContactsToReach = (contacts: ProjectContactLink[]) =>
  [...contacts].sort(
    (a, b) =>
      Number(!!a.funder_only) - Number(!!b.funder_only) ||
      Number(!!b.project_mention) - Number(!!a.project_mention) ||
      confidence(b) - confidence(a) ||
      Number(reachable(b)) - Number(reachable(a)),
  );

/** Contact à joindre en premier, pour l'en-tête de la fiche : joignable, hors bailleurs, le mieux placé. */
export const bestContact = (contacts: ProjectContactLink[]) =>
  sortContactsToReach(
    contacts.filter((c) => !c.funder_only && reachable(c)),
  )[0];

// Personne rendue par la recherche de l'équipe d'une société (fournisseur), encore sans place dans la chaîne ni
// coordonnée : rangée à part pour ne pas noyer les contacts à joindre (jusqu'à 150 par société).
const isTeamMember = (contact: ProjectContactLink) =>
  contact.kind === "person" &&
  !contact.decision_role &&
  !contact.project_mention &&
  !contact.email_jsonb?.length &&
  !contact.phone_jsonb?.length &&
  contact.evidence.some((e) => e.field === "name" && e.method === "provider");

const fold = (text: string) =>
  text.normalize("NFKD").replace(/\p{M}/gu, "").toLowerCase();

export type TeamGroup = { company: string; members: ProjectContactLink[] };

/** Contacts à joindre d'un côté ; équipes des sociétés de l'autre, par société puis par fonction, filtrées sur la
 * fonction ou le nom (`query`, sans accents) ; `count` : membres d'équipe avant le filtre. */
export const splitTeam = (contacts: ProjectContactLink[], query: string) => {
  const others = contacts.filter((c) => !isTeamMember(c));
  const team = contacts.filter(isTeamMember);
  const wanted = fold(query.trim());
  const byCompany = new Map<string, ProjectContactLink[]>();
  for (const member of team) {
    const text = fold(
      [member.title, member.first_name, member.last_name].join(" "),
    );
    if (wanted && !text.includes(wanted)) continue;
    const company = member.company_name ?? "";
    byCompany.set(company, [...(byCompany.get(company) ?? []), member]);
  }
  const groups: TeamGroup[] = [...byCompany.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([company, members]) => ({
      company,
      members: [...members].sort((a, b) =>
        (a.title ?? "").localeCompare(b.title ?? ""),
      ),
    }));
  return { others, groups, count: team.length };
};

/** Identifiants des personnes rangées dans les équipes des sociétés. */
export const teamMemberIds = (contacts: ProjectContactLink[]) =>
  new Set(contacts.filter(isTeamMember).map((c) => c.contact_id));

/** Personnes rangées dans les équipes à la lecture précédente (`before`) qui n'y sont plus : elles ont reçu un e-mail,
 * un téléphone ou une place dans la chaîne, et sont passées dans les contacts de la fiche. */
export const leftTeam = (
  before: Set<ProjectContactLink["contact_id"]> | null,
  contacts: ProjectContactLink[],
) =>
  before
    ? contacts.filter((c) => before.has(c.contact_id) && !isTeamMember(c))
    : [];

const WEEK = 7 * 86_400_000;

/** Personne trouvée par la recherche de l'équipe d'une société, dont une coordonnée a été obtenue depuis moins de 7
 * jours : la plus récente (e-mail ou téléphone), pour la signaler dans les contacts de la fiche ; sinon `null`. */
export const joinedFromTeam = (
  contact: ProjectContactLink,
  now = Date.now(),
): { field: "email" | "phone"; date: string } | null => {
  if (
    !contact.evidence.some((e) => e.field === "name" && e.method === "provider")
  )
    return null;
  const latest = contact.evidence
    .filter((e) => e.field === "email" || e.field === "phone")
    .sort((a, b) => Date.parse(b.collected_at) - Date.parse(a.collected_at))[0];
  if (!latest || now - Date.parse(latest.collected_at) > WEEK) return null;
  return {
    field: latest.field as "email" | "phone",
    date: latest.collected_at,
  };
};

/** Date d'une recherche par personne qui a trouvé cette donnée depuis moins de 7 jours, sinon rien. */
export const freshFind = (
  lookups: ContactLookup[] | undefined,
  kind: ContactLookup["kind"],
  now = Date.now(),
): string | null => {
  const found = (lookups ?? [])
    .filter(
      (l) =>
        l.kind === kind && l.status === "done" && l.found && l.processed_at,
    )
    .map((l) => l.processed_at as string)
    .sort((a, b) => Date.parse(b) - Date.parse(a))[0];
  return found && now - Date.parse(found) <= WEEK ? found : null;
};
